import type { MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

export interface CarouselNavigateEvent {
  swiped: boolean;
}

const debounce = (func: () => void, ms: number) => {
  let timeout: ReturnType<typeof setTimeout> | null;

  return () => {
    clearTimeout(timeout as NodeJS.Timeout);

    timeout = setTimeout(() => {
      timeout = null;
      func();
    }, ms);
  };
};

const hasPreviousItem = (carouselRef: MutableRefObject<HTMLDivElement | null>) => {
  if (carouselRef.current && carouselRef.current.scrollLeft > 0) {
    return true;
  }
  return false;
};

const hasNextItem = (carouselRef: MutableRefObject<HTMLDivElement | null>) => {
  if (
    carouselRef.current &&
    carouselRef.current.scrollWidth - carouselRef.current.scrollLeft >
      carouselRef.current.offsetWidth + 1
  ) {
    return true;
  }
  return false;
};

export const useCarousel = ({
  columnGap,
  forceNextItemControlEnabled,
  forcePreviousItemControlEnabled,
  itemCount,
  onNavigateLeft,
  onNavigateRight,
}: {
  columnGap: number;
  forceNextItemControlEnabled?: boolean;
  forcePreviousItemControlEnabled?: boolean;
  itemCount: number;
  onNavigateLeft?: (details: CarouselNavigateEvent) => void;
  onNavigateRight?: (details: CarouselNavigateEvent) => void;
}) => {
  const [showPreviousControl, setShowPreviousControl] = useState(false);
  const [showNextControl, setShowNextControl] = useState(false);
  const xPosition = useRef<number>(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const columnGapRef = useRef<number>(0);
  const forcePreviousItemControlEnabledRef = useRef<boolean | undefined>();
  const forceNextItemControlEnabledRef = useRef<boolean | undefined>();
  // Track whether carousel navigations were triggered by swipe (default) or controls (set when controls used)
  const isSwipeNavigation = useRef<boolean>(true);

  // Assign params to refs so the updated values can be accessed in the scroll handler
  useEffect(() => {
    columnGapRef.current = columnGap;
    forcePreviousItemControlEnabledRef.current = forcePreviousItemControlEnabled;
    forceNextItemControlEnabledRef.current = forceNextItemControlEnabled;
  });

  useEffect(() => {
    // Maintain carouselRefValue for cleanup to avoid ref instance error
    const carouselRefValue = carouselRef.current;

    if (carouselRef.current) {
      // Setup controls
      if (hasNextItem(carouselRef) || Boolean(forceNextItemControlEnabledRef.current) === true) {
        setShowNextControl(true);
      }

      // Scroll listener for managing visibility of controls and triggering callbacks
      const handleScroll = debounce(() => {
        if (!carouselRef.current) {
          return;
        }

        // Check whether carousel is at a scroll position or between items (e.g. currently swiping)
        const itemWidth =
          (carouselRef.current.scrollWidth - columnGapRef.current * (itemCount - 1)) / itemCount;

        const atScrollEndPosition =
          carouselRef.current.scrollWidth - carouselRef.current.scrollLeft ===
          carouselRef.current.offsetWidth;

        const atNonEndScrollPosition =
          carouselRef.current.scrollLeft % (itemWidth + columnGapRef.current) === 0;
        const atScrollPosition = atScrollEndPosition || atNonEndScrollPosition;

        /*
          Note - these events will fire on window resize. This could be avoided by wrapping this event listener in a
          setTimeout 0, and setting a flag in a window resize event handler to signal that a resize triggered the
          scroll handler.
          https://stackoverflow.com/questions/45635008/how-can-i-prevent-onscroll-function-to-trigger-during-resize
        */
        // Note: only fire these when swiping, as they have already been fired for control based navigation
        if (
          isSwipeNavigation.current === true &&
          carouselRef.current.scrollLeft < xPosition.current &&
          atScrollPosition
        ) {
          onNavigateLeft?.({ swiped: true });
        } else if (
          isSwipeNavigation.current === true &&
          carouselRef.current.scrollLeft > xPosition.current &&
          atScrollPosition
        ) {
          onNavigateRight?.({ swiped: true });
        }

        // Once event handlers triggered, reset isSwipe to default
        isSwipeNavigation.current = true;
        xPosition.current = carouselRef.current.scrollLeft;

        if (hasNextItem(carouselRef) || Boolean(forceNextItemControlEnabledRef.current) === true) {
          setShowNextControl(true);
        } else {
          setShowNextControl(false);
        }

        if (hasPreviousItem(carouselRef) || forcePreviousItemControlEnabledRef.current) {
          setShowPreviousControl(true);
        } else {
          setShowPreviousControl(false);
        }
      }, 200);

      carouselRefValue?.addEventListener('scroll', handleScroll);

      return () => {
        carouselRefValue?.removeEventListener('scroll', handleScroll);
      };
    }

    return () => {};
    // Note: (Performance) having onNavigateLeft and onNavigateRight causes this hook to run on every re-render
    // (e.g. window resize)
    // To prevent this, the onNavigate function could be wrapped in useCallback - this requires parent carousels to not
    // then wrap this function in another function
  }, [itemCount, onNavigateLeft, onNavigateRight, showNextControl, showPreviousControl]);

  return {
    showPreviousControl,
    showNextControl,
    isSwipeNavigation,
    carouselRef,
  };
};

// Encapsulate scrollTo in util for testing purposes as jest dom doesn't support it
export const smoothScrollCarousel = (
  carouselRef: MutableRefObject<HTMLDivElement | null>,
  scrollLeft: number,
) => {
  // Ensure ref exists and that scrollTo exists (it doesn't work in tests and in some instances mocking is tricky)
  // eslint-disable-next-line
  if (carouselRef.current && carouselRef.current.scrollTo) {
    carouselRef.current.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }
};

export const useCarouselControls = ({
  carouselRef,
  columnGap,
  isSwipeNavigation,
  itemCount,
  onNavigateLeft,
  onNavigateRight,
  smoothScrollCarouselDI,
}: {
  carouselRef: MutableRefObject<HTMLDivElement | null>;
  columnGap: number;
  isSwipeNavigation: MutableRefObject<boolean>;
  itemCount: number;
  onNavigateLeft?: (details: CarouselNavigateEvent) => void;
  onNavigateRight?: (details: CarouselNavigateEvent) => void;
  // Inject smoothScrollCarousel as a dependency to enable mocking in tests
  smoothScrollCarouselDI: (
    carouselRef: MutableRefObject<HTMLDivElement | null>,
    scrollLeft: number,
  ) => void;
}) => {
  const columnGapRef = useRef<number>(0);

  // Assign params to refs so the updated values can be accessed in the callbacks
  useEffect(() => {
    columnGapRef.current = columnGap;
  });

  const onPrevious: () => void = () => {
    if (!carouselRef.current) {
      return;
    }

    isSwipeNavigation.current = false;
    const scrollWidthWithoutColumnGaps =
      carouselRef.current.scrollWidth - columnGapRef.current * (itemCount - 1);
    const scrollLeft = Math.floor(
      carouselRef.current.scrollLeft -
        scrollWidthWithoutColumnGaps / itemCount -
        columnGapRef.current,
    );

    /*
      Note: scrollLeft is slightly off for first left scroll from right end due to un-uniform scroll position at this
      point. However, it is snapped to position regardless by the browser later, and so downstream functionality works
      correctly.
    */
    smoothScrollCarouselDI(carouselRef, scrollLeft);
    onNavigateLeft?.({ swiped: false });
  };

  const onNext: () => void = () => {
    if (!carouselRef.current) {
      return;
    }

    isSwipeNavigation.current = false;
    const scrollWidthWithoutColumnGaps =
      carouselRef.current.scrollWidth - columnGapRef.current * (itemCount - 1);
    const scrollLeft = Math.floor(
      carouselRef.current.scrollLeft +
        scrollWidthWithoutColumnGaps / itemCount +
        columnGapRef.current,
    );

    smoothScrollCarouselDI(carouselRef, scrollLeft);
    onNavigateRight?.({ swiped: false });
  };

  return {
    onPrevious,
    onNext,
  };
};
